import { Mail, ListFilter, Users, List, Tags, Map, Calendar, HelpCircle, BarChart3 } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ContactSearchDropdown } from "./ContactSearchDropdown";
import { WorkspaceNav } from "./WorkspaceNav";
import { ModeToggle } from "./ModeToggle";
import { NavHoverMenu } from "./NavHoverMenu";
import { FilterNavMenu } from "./FilterNavMenu";
import { GroupNavMenu } from "./GroupNavMenu";
import { ContactNavMenu } from "./ContactNavMenu";
import { CalendarNavMenu } from "./CalendarNavMenu";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { isHermesUiEnabled } from "@/lib/feature-flags";

const NAV_MENU_MAP: Record<string, React.ComponentType> = {
  Contacts: ContactNavMenu,
  Filters: FilterNavMenu,
  Groups: GroupNavMenu,
  Calendar: CalendarNavMenu,
};

const allMainNavItems = [
  {
    title: "Contacts",
    href: "/contacts",
    icon: Users,
  },
  {
    title: "Filters",
    href: "/filters",
    icon: ListFilter,
  },
  {
    title: "Groups",
    href: "/groups",
    icon: Tags,
  },
  {
    title: "Calendar",
    href: "/calendar",
    icon: Calendar,
  },
  {
    title: "Email",
    href: "/inbox",
    icon: Mail,
  },
  {
    title: "Stats",
    href: "/analytics",
    icon: BarChart3,
  },
];

// Filter Email out at module load if Hermes UI is gated off (Phase 3 fix 3.D).
// Vite inlines import.meta.env at build time, so this resolves once per build.
const mainNavItems = isHermesUiEnabled()
  ? allMainNavItems
  : allMainNavItems.filter((item) => item.title !== "Email");

interface MainNavProps {
  onSearch?: (value: string) => void;
  searchValue?: string;
}

export function MainNav({ onSearch, searchValue = "" }: MainNavProps) {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [internalSearchValue, setInternalSearchValue] = useState(searchValue);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Keep internal search value synced with prop
  useEffect(() => {
    setInternalSearchValue(searchValue);
  }, [searchValue]);

  // Handle search change without resetting the value
  const handleSearchChange = (value: string) => {
    console.log('MainNav handleSearchChange called with:', value);
    setInternalSearchValue(value);
    if (onSearch) {
      onSearch(value); // Pass along to parent without modifying
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="w-full">
        <div className="flex h-14 items-center px-4">
          {/* Mobile Menu Button */}
          {isMobile && (
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden mr-2">
                  <List className="h-5 w-5" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[80%] max-w-sm py-6">
                <nav className="flex flex-col space-y-1 mt-4">
                  {mainNavItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.href;
                    return (
                      <Button
                        key={item.href}
                        asChild
                        variant={isActive ? "default" : "ghost"}
                        size="sm"
                        className={cn(
                          "justify-start",
                          isActive && "bg-primary text-primary-foreground hover:bg-primary/90"
                        )}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <Link to={item.href} className="flex items-center gap-x-2 py-2">
                          <Icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </Link>
                      </Button>
                    );
                  })}
                </nav>
              </SheetContent>
            </Sheet>
          )}
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {mainNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              const MenuContent = NAV_MENU_MAP[item.title];

              const button = (
                <Button
                  asChild
                  variant={isActive ? "default" : "ghost"}
                  size="sm"
                  className={cn(
                    "h-8",
                    isActive && "bg-primary text-primary-foreground hover:bg-primary/90"
                  )}
                >
                  <Link to={item.href} className="flex items-center gap-x-2">
                    <Icon className="h-4 w-4" />
                    <span>{item.title}</span>
                  </Link>
                </Button>
              );

              if (MenuContent) {
                return (
                  <NavHoverMenu key={item.href} content={<MenuContent />}>
                    {button}
                  </NavHoverMenu>
                );
              }

              return <div key={item.href}>{button}</div>;
            })}
          </nav>
          
          {/* Search Bar - For navigation only, not filtering */}
          <div className="flex-1 flex justify-center px-2 sm:px-4 max-w-2xl">
            <ContactSearchDropdown 
              searchValue={internalSearchValue} 
              onSearchChange={handleSearchChange}
              className="w-full"
              showOnAllPages={true}
            />
          </div>
          
          <div className="ml-auto flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <Link to="/visualization">
                <Map className="h-4 w-4" />
              </Link>
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <Link to="/help">
                <HelpCircle className="h-4 w-4" />
              </Link>
            </Button>
            <NotificationBell />
            <ModeToggle />
            <WorkspaceNav />
          </div>
        </div>
      </div>
    </header>
  );
}